/**
 * Customer-side Quixo expert review components (track T10). Data: GET /v1/audit-remediation/expert/jobs/:jobId.
 *
 *  ExpertSignoffBadge   "Reviewed by Quixo expert <name>, <date>" (only for an approved sign-off) or the review status.
 *  ExpertFindingNotes   customer-visible expert notes + the expert severity change for ONE finding (finding drawer).
 *  ExpertReviewPanel    report-level card: badge, Essential "get an expert review" (checkout handoff), bookings with
 *                       cancel, and "book an expert" (BookExpertDialog).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Badge, Button, ErrorState, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import {
  advisorApi, bookingErrorKey, customerCanCancel, formatDay, formatWhen,
  type AdvisorApi, type Booking, type CheckoutHandoff, type CustomerNote, type ExpertView, type ReviewStatus, type SeverityOverride, type SignoffBadge,
} from './api';
import { BookExpertDialog } from './BookExpertDialog';
import './advisor.css';

export function ExpertSignoffBadge({ signoff, status }: { signoff: SignoffBadge | null; status: ReviewStatus }) {
  const { t, locale } = useApp();
  if (signoff) {
    return (
      <span className="qm-expert-badge" data-testid="expert-signoff">
        <Badge tone="success">{t('audit-remediation.expert.badge.reviewed', { name: signoff.reviewerName, date: formatDay(signoff.signedAt, locale) })}</Badge>
      </span>
    );
  }
  if (status === 'not_requested') return null;
  return <Badge tone={status === 'changes_requested' ? 'warning' : 'neutral'}>{t(`audit-remediation.expert.status.${status}`)}</Badge>;
}

export interface ExpertFindingNotesProps { findingId: string; notes: readonly CustomerNote[]; overrides: readonly SeverityOverride[]; headingLevel?: 3 | 4 }

export function ExpertFindingNotes({ findingId, notes, overrides, headingLevel = 4 }: ExpertFindingNotesProps) {
  const { t, locale } = useApp();
  const id = useId();
  const mine = notes.filter((n) => n.findingId === findingId);
  const override = overrides.find((o) => o.findingId === findingId) ?? null;
  if (!mine.length && !override) return null;
  const H = `h${headingLevel}` as 'h3' | 'h4';
  return (
    <section className="qm-expert-notes" aria-labelledby={`${id}-h`} data-testid="expert-notes">
      <H id={`${id}-h`} className="qm-expert-notes__title">{t('audit-remediation.expert.notes.title')}</H>
      {override ? (
        <p className="qm-expert-notes__override">
          {t('audit-remediation.expert.notes.severityChanged', { from: t(`audit-remediation.severity.${override.from}`), to: t(`audit-remediation.severity.${override.to}`) })}
          {' '}<span dir="auto">{override.reason}</span>
        </p>
      ) : null}
      <ul className="qm-expert-notes__list">
        {mine.map((n) => (
          <li key={n.id}>
            <p dir="auto">{n.body}</p>
            <p className="qm-expert-notes__meta">{t('audit-remediation.expert.notes.by', { date: formatWhen(n.updatedAt, locale) })}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ExpertReviewPanelProps {
  jobId: string;
  /** owner / admin may buy a review; owner / admin / member may book and cancel their own bookings */
  canBuy?: boolean;
  canBook?: boolean;
  onCheckout?: (c: CheckoutHandoff) => void;
  initial?: { view: ExpertView; bookings: Booking[] };
  api?: AdvisorApi;
  now?: () => Date;
}

export function ExpertReviewPanel({ jobId, canBuy = false, canBook = false, onCheckout, initial, api, now = () => new Date() }: ExpertReviewPanelProps) {
  const { t, locale } = useApp();
  const client = useMemo(() => api ?? advisorApi(), [api]);
  const id = useId();
  const [view, setView] = useState<ExpertView | null>(initial?.view ?? null);
  const [bookings, setBookings] = useState<Booking[]>(initial?.bookings ?? []);
  const [error, setError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<CheckoutHandoff | null>(null);
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) return;
    let live = true;
    client.expert(jobId).then((v) => { if (live) setView(v); }, (e: unknown) => { if (live) setError(bookingErrorKey(e)); });
    client.bookings().then((b) => { if (live) setBookings(b.filter((x) => x.jobId === jobId || x.jobId === null)); }, () => undefined);
    return () => { live = false; };
  }, [client, jobId, initial]);

  async function requestReview() {
    setBusy(true);
    setError(null);
    try {
      const r = await client.requestReview(jobId);
      if (r.checkout) { setCheckout(r.checkout); onCheckout?.(r.checkout); }
      setView((v) => (v ? { ...v, review: { ...v.review, status: r.status } } : v));
    } catch (e) { setError(bookingErrorKey(e)); } finally { setBusy(false); }
  }

  async function cancel(b: Booking) {
    setBusy(true);
    setError(null);
    try {
      const next = await client.cancel(b.id);
      setBookings((cur) => cur.map((x) => (x.id === next.id ? next : x)));
    } catch (e) { setError(bookingErrorKey(e)); } finally { setBusy(false); }
  }

  return (
    <section className="qm-expert" aria-labelledby={`${id}-h`} data-testid="expert-review">
      <h2 id={`${id}-h`} className="qm-expert__title">{t('audit-remediation.expert.panel.title')}</h2>
      {!view && !error ? <Loading label={t('audit-remediation.expert.panel.loading')} /> : null}
      {view ? (
        <>
          <div className="qm-expert__row">
            <ExpertSignoffBadge signoff={view.review.signoff} status={view.review.status} />
            <p className="qm-expert__explain">{t(view.review.included ? 'audit-remediation.expert.panel.included' : 'audit-remediation.expert.panel.addon')}</p>
          </div>
          {view.review.signoff ? <p className="qm-expert__summary" dir="auto">{locale === 'ar' && view.review.signoff.summary.ar ? view.review.signoff.summary.ar : view.review.signoff.summary.en}</p> : null}
          {!view.review.included && view.review.status === 'not_requested' && canBuy ? (
            <Button variant="primary" onClick={requestReview} disabled={busy}>{t('audit-remediation.expert.panel.buyReview')}</Button>
          ) : null}
          {checkout || view.review.status === 'awaiting_payment' ? <p className="qm-expert__pending" role="status">{t('audit-remediation.expert.panel.awaitingPayment')}</p> : null}
        </>
      ) : null}
      {error ? <ErrorState message={t(error)} /> : null}

      <h3 className="qm-expert__sub">{t('audit-remediation.expert.bookings.title')}</h3>
      {bookings.length ? (
        <ul className="qm-expert__bookings">
          {bookings.map((b) => (
            <li key={b.id} className="qm-expert__booking">
              <p dir="auto">{b.topic}</p>
              <p className="qm-expert__meta">
                <Badge tone={b.status === 'confirmed' ? 'success' : b.status === 'cancelled' ? 'neutral' : 'accent'}>{t(`audit-remediation.expert.bookingStatus.${b.status}`)}</Badge>
                {' '}{b.scheduledAt ? formatWhen(b.scheduledAt, locale) : t('audit-remediation.expert.bookings.unscheduled')}
              </p>
              {canBook && customerCanCancel(b, now()) ? (
                <Button variant="ghost" onClick={() => cancel(b)} disabled={busy} aria-label={t('audit-remediation.expert.bookings.cancelFor', { topic: b.topic })}>
                  {t('audit-remediation.expert.bookings.cancel')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : <p>{t('audit-remediation.expert.bookings.none')}</p>}
      {canBook ? <Button variant="secondary" onClick={() => setDialog(true)}>{t('audit-remediation.expert.bookings.book')}</Button> : null}
      {dialog ? (
        <BookExpertDialog jobId={jobId} api={client} onClose={() => setDialog(false)}
          onCheckout={(c, b) => { setBookings((cur) => [b, ...cur]); onCheckout?.(c); }} />
      ) : null}
    </section>
  );
}
